import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PHASE_INFO from '../data/hormonalCyclePhases';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_LABELS = {
  energy: 'Energía',
  hunger: 'Hambre',
  cravings: 'Cravings',
  bloating: 'Inflamación',
  sleep: 'Sueño',
  menstrual_pain: 'Dolor menstrual',
  workout_motivation: 'Motivación entreno',
  perceived_performance: 'Rendimiento',
  mood: 'Ánimo'
};

const STATUS_LABELS = {
  '21-24': '21–24 días',
  '25-28': '25–28 días',
  '29-32': '29–32 días',
  '33+': '33+ días',
  'no_segura': 'No segura',
  'regular': 'Regular',
  'irregular': 'Irregular',
  'si': 'Sí',
  'no': 'No',
  'prefer_no_answer': 'Prefiere no responder',
  '2-3': '2–3 días',
  '4-5': '4–5 días',
  '6-7': '6–7 días',
  'more_7': 'Más de 7 días'
};

// Panel admin que muestra el estado del ciclo hormonal de una cliente.
// Se renderiza en el offline generator cuando se selecciona una cliente por email.
export default function HormonalCycleAdminPanel({ email }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email || !email.includes('@')) return;
    setLoading(true);
    axios.get(`${API}/admin/hormonal-cycle/by-email/${encodeURIComponent(email)}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [email]);

  if (loading) {
    return (
      <div className="p-4 rounded-xl text-xs text-gray-500" style={{ background: 'rgba(192, 132, 252, 0.04)', border: '1px solid rgba(192, 132, 252, 0.1)' }}>
        Cargando ciclo hormonal...
      </div>
    );
  }

  if (!data || !data.found) return null;

  // Cliente no ha activado el módulo
  if (!data.enabled) {
    return (
      <div className="p-4 rounded-xl text-xs" style={{ background: 'rgba(192, 132, 252, 0.06)', border: '1px solid rgba(192, 132, 252, 0.15)' }} data-testid="hormonal-admin-panel-empty">
        <div className="flex items-center gap-2">
          <span className="text-xl">🌙</span>
          <div>
            <p className="font-semibold text-purple-300">Mi Ciclo Hormonal</p>
            <p className="text-gray-400">La cliente aún no ha activado este módulo.</p>
          </div>
        </div>
      </div>
    );
  }

  const phaseKey = data.current_estimated_phase;
  const phaseData = phaseKey ? PHASE_INFO[phaseKey] : null;
  const phaseInfo = data.phase_info;
  const averages = data.averages_last_14_days || {};
  const isIrregular = data.cycle_regular_status === 'irregular';

  // Colorear promedio: bajo (rojo) / medio (amarillo) / alto (verde)
  const avgColor = (v) => {
    if (v === null || v === undefined) return '#6b7280';
    if (v <= 2) return '#ef4444';
    if (v <= 3.5) return '#f59e0b';
    return '#22c55e';
  };

  return (
    <div className="p-4 rounded-xl space-y-3" style={{ background: 'linear-gradient(135deg, rgba(192, 132, 252, 0.08), rgba(var(--brand-primary-glow), 0.04))', border: '1px solid rgba(192, 132, 252, 0.25)' }} data-testid="hormonal-admin-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🌙</span>
          <p className="font-bold text-purple-300 text-sm">Mi Ciclo Hormonal — Resumen Cliente</p>
        </div>
        {data.total_checkins > 0 && (
          <span className="text-xs text-gray-500">{data.total_checkins} check-ins totales</span>
        )}
      </div>

      {/* Fase actual */}
      {phaseData && (
        <div className="p-3 rounded-lg" style={{ background: `${phaseData.color}15`, border: `1px solid ${phaseData.color}40` }}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{phaseData.emoji}</span>
              <div>
                <p className="text-xs uppercase tracking-wider" style={{ color: phaseData.color }}>Fase estimada</p>
                <p className="font-bold text-white text-base" data-testid="admin-current-phase">{phaseData.name}</p>
              </div>
            </div>
            {phaseInfo && (
              <div className="text-right text-xs text-gray-400">
                <p>Día {phaseInfo.day_in_cycle}/{phaseInfo.cycle_length_days}</p>
                <p>Próximo periodo: {phaseInfo.days_until_next_period}d</p>
              </div>
            )}
          </div>
          {isIrregular && (
            <p className="text-xs text-amber-400 mt-2">⚠️ Ciclo irregular — la fase es solo una estimación</p>
          )}
        </div>
      )}

      {/* Datos del ciclo */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="p-2 rounded bg-white/5">
          <p className="text-gray-500">Último periodo</p>
          <p className="text-white font-semibold">{data.last_period_start_date || '—'}</p>
        </div>
        <div className="p-2 rounded bg-white/5">
          <p className="text-gray-500">Duración ciclo</p>
          <p className="text-white font-semibold">{STATUS_LABELS[data.average_cycle_length] || '—'}</p>
        </div>
        <div className="p-2 rounded bg-white/5">
          <p className="text-gray-500">Sangrado</p>
          <p className="text-white font-semibold">{STATUS_LABELS[data.average_bleeding_days] || '—'}</p>
        </div>
        <div className="p-2 rounded bg-white/5">
          <p className="text-gray-500">Regularidad</p>
          <p className="text-white font-semibold">{STATUS_LABELS[data.cycle_regular_status] || '—'}</p>
        </div>
        <div className="p-2 rounded bg-white/5 col-span-2">
          <p className="text-gray-500">Anticonceptivos hormonales</p>
          <p className="text-white font-semibold">{STATUS_LABELS[data.hormonal_contraceptive_status] || '—'}</p>
        </div>
      </div>

      {/* Síntomas */}
      {data.symptoms?.length > 0 && (
        <div className="text-xs">
          <p className="text-gray-500 mb-1">Síntomas frecuentes reportados</p>
          <div className="flex flex-wrap gap-1">
            {data.symptoms.map((s, i) => (
              <span key={i} className="px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(192, 132, 252, 0.15)', color: '#d8b4fe' }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Promedios últimos 14 días */}
      {Object.values(averages).some(v => v !== null) && (
        <div>
          <p className="text-gray-500 text-xs mb-2">📊 Promedio últimos 14 días (escala 1–5)</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {Object.entries(FIELD_LABELS).map(([key, label]) => (
              <div key={key} className="p-2 rounded bg-white/5">
                <p className="text-gray-400 text-[10px] truncate">{label}</p>
                <p className="font-bold" style={{ color: avgColor(averages[key]) }} data-testid={`avg-${key}`}>
                  {averages[key] !== null && averages[key] !== undefined ? averages[key] : '—'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Últimos check-ins */}
      {data.recent_checkins?.length > 0 && (
        <div>
          <p className="text-gray-500 text-xs mb-2">📅 Últimos {Math.min(7, data.recent_checkins.length)} check-ins</p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.recent_checkins.slice(0, 7).map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs p-2 rounded bg-white/5">
                <span className="text-gray-400 w-20 flex-shrink-0">{c.date}</span>
                <div className="flex gap-2 flex-wrap text-[10px]">
                  {Object.entries(FIELD_LABELS).map(([k, label]) => (
                    c[k] !== null && c[k] !== undefined && (
                      <span key={k} className="text-gray-300" title={label}>
                        <span className="text-gray-500">{label.slice(0, 4)}:</span>
                        <span className="font-bold ml-1" style={{ color: avgColor(c[k]) }}>{c[k]}</span>
                      </span>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-gray-500 italic">
        Esta información es una estimación basada en lo que reporta la cliente. Úsala como referencia para personalizar mejor la dieta y el entrenamiento.
      </p>
    </div>
  );
}
